import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, FINANCE, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const RevertCreditForm = NiceModal.create((props) => {
	const { id, modalName, transactionStatus } = props;
	const { t } = useTranslation();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();

	const schema = yup.object().shape({
		deliveryCompanySettingsId: yup.number().nullable(),
		shipmentId: yup.string().nullable(),
		shipmentType: yup.string().nullable(),
		trackingUrl: yup.string().nullable(),
		status: yup.string().nullable(),
		amount: yup.number().nullable()
	});

	const fillForm = async () => {
		const defaultValues = {
			deliveryCompanySettingsId: null,
			shipmentId: null,
			shipmentType: null,
			trackingUrl: null,
			status: null,
			amount: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(FINANCE.GET_TRANSACTION_INFORMATION_FOR_UPDATE, {
				id: Number(id)
			});
			const { data, status } = response;

			if (status === 200) {
				return {
					...defaultValues,
					...data
				};
			} else AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error.response?.data?.code
			});
			modal.remove();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isLoading);

	const onSubmit = async () => {
		try {
			const URL = transactionStatus === 'booked' ? FINANCE.CANCEL_TRANSACTION : FINANCE.REACTIVATE_TRANSACTION;
			const response = await axiosPrivate.post(URL, {
				id: Number(id)
			});

			const { status } = response;

			if (status === 200) {
				showSuccess();
				modal.remove();
				dispatch(updateDynamicListsInPage());
			} else AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error.response?.data?.code
			});
		}
	};

	const fetchAutocompleteData = useCallback(async (props) => {
		const data = await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_DELIVERY_COMPANY_AUTOCOMPLETE)
			.then((response) => response.data)
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return data.filter((item) =>
			props?.keyword
				? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	return (
		<Modal fullwidth maxWidth="x-large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">
					{transactionStatus === 'booked' ? t('cancelTransactionTitle') : t('reactivateTransactionTitle')} (
					<strong>{id}</strong>)
				</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem
						label={
							watch('shipmentType') === 'forward'
								? 'forwardDeliveryCompanySettingsId'
								: 'reverseDeliveryCompanySettingsId'
						}
						isLoading={isLoading}>
						<AutoComplete
							fetchFunction={fetchAutocompleteData}
							value={watch('deliveryCompanySettingsId')}
							name={'deliveryCompanySettingsId'}
							selectionLabelAccessor={['ID', 'name']}
							columns={[
								{ accessor: 'ID', width: 100 },
								{ accessor: 'name', width: 1000 }
							]}
							disabled
						/>
					</FormItem>
					<FormItem label={watch('shipmentType') === 'forward' ? 'forwardShipmentId' : 'reverseShipmentId'}>
						<StringInput name="shipmentId" disabled />
					</FormItem>

					<FormItem label="status">
						<EnumSelect
							name="status"
							enumName={ENUMS.ALL_STATUSES}
							valueRemovable={false}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							disabled
						/>
					</FormItem>
					<FormItem label="trackingUrl">
						<StringInput name="trackingUrl" disabled />
					</FormItem>
					<FormItem label="shippingCost">
						<IntegerInput name="amount" disabled />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={() => {
						modal.remove();
					}}
				/>

				<Button
					label={transactionStatus === 'booked' ? t('cancelTransaction') : t('reactivateTransaction')}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default RevertCreditForm;
